/**
 * Arm-guard (#856): the sheet layout activates only while EXACTLY ONE
 * eligible explorer instance exists in the document.
 *
 * The toolkit refuses to activate unless there is exactly one
 * `.hfse.with-map` on the page (explorer-mobile-sheet.js:149-167,
 * `findTarget` → `bail: count`). Two fixed full-screen sheets cannot share a
 * viewport, and a first-claimant-wins slot would strand the second widget
 * and the host page behind the winner's fixed map + scroll lock (codex fresh
 * pass). So instances REGISTER as contenders and each activates only while
 * the contender count is one; a second registration deactivates the first,
 * and its departure re-activates it. Module-level on purpose: every explorer
 * instance on a page shares this bundle.
 */
type Listener = () => void

const contenders = new Set<symbol>()
const listeners = new Set<Listener>()

function notify(): void {
  for (const listener of [...listeners]) listener()
}

/** Register an eligible instance; returns the unregister function. */
export function registerSheetContender(token: symbol): () => void {
  if (!contenders.has(token)) {
    contenders.add(token)
    notify()
  }
  return () => {
    if (contenders.delete(token)) notify()
  }
}

export function sheetContenderCount(): number {
  return contenders.size
}

export function subscribeSheetOwner(listener: Listener): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** Test hook: forget every contender between test files. */
export function __resetSheetOwnerForTests(): void {
  contenders.clear()
  listeners.clear()
}
